import { useTranslation } from 'react-i18next';

// Import style
import { InvoicePlan, InvoicePlanStatus } from '@wonderschool/common-base-types';
import { Button } from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import { userHasPermission } from '../../../../api/firebase/account';
import { getUpdatedBillingDates } from '../../../../helpers/invoicePlans';
import { selectFirstLocation } from '../../../DateTime/DateTime';
import '../InvoiceDetail/InvoiceDetail.scss';

type InvoicePlanActionButtonsProps = {
  showActionButtons: boolean;
  loading: boolean;
  selectedInvoicePlan: InvoicePlan & { intervalTransitionRecovery?: { attemptId?: string } };
  onChangeStatus: (status: InvoicePlanStatus, updates?: Partial<InvoicePlan>) => void;
  onClickEdit: () => void;
};

export function InvoicePlanActionButtons({
  showActionButtons,
  loading,
  selectedInvoicePlan,
  onChangeStatus,
  onClickEdit,
}: InvoicePlanActionButtonsProps) {
  const { t } = useTranslation();
  const { isEndBillingPlanEnabled } = useFlags();
  const firstLocation = useSelector(selectFirstLocation);
  const timezone = firstLocation?.timezone ?? 'utc';
  const isActive = selectedInvoicePlan?.status === InvoicePlanStatus.ACTIVE;
  const isPaused = selectedInvoicePlan?.status === InvoicePlanStatus.PAUSED;
  const hasPlanEnd = Boolean(selectedInvoicePlan?.dateEnd);
  const isScheduled = selectedInvoicePlan?.status === InvoicePlanStatus.SCHEDULED;
  return (
    <div className="flex gap-2">
      {showActionButtons && userHasPermission('can_edit_billing') && (
        <>
          {isPaused && !selectedInvoicePlan.intervalTransitionRecovery && (!isEndBillingPlanEnabled || !hasPlanEnd) && (
            <Button
              data-testid="resume-invoice-plan-button"
              primary
              loading={loading}
              disabled={loading}
              onClick={() => {
                const dates = getUpdatedBillingDates({
                  invoicePlan: selectedInvoicePlan,
                  timezone,
                  isEditing: true,
                });
                onChangeStatus(InvoicePlanStatus.ACTIVE, {
                  dateDue: dates.dateDue,
                  dateSend: dates.dateSend,
                  isArchived: false,
                });
              }}
            >
              {t('Resume Invoice Plan')}
            </Button>
          )}

          {isActive && (
            <Button
              data-testid="pause-invoice-plan-button"
              primary
              loading={loading}
              disabled={loading}
              onClick={() => onChangeStatus(InvoicePlanStatus.PAUSED)}
            >
              {t('Pause Invoice Plan')}
            </Button>
          )}

          {isActive && !isScheduled && (
            <Button
              data-testid="send-invoices-now-button"
              primary
              loading={loading}
              disabled={loading}
              onClick={() => {
                onChangeStatus(InvoicePlanStatus.SCHEDULED, { dateSend: Date.now() });
              }}
            >
              {t('Send Invoices Now')}
            </Button>
          )}

          <Button primary loading={loading} disabled={loading} onClick={onClickEdit} data-testid="edit-invoice-plan">
            {t('common.edit')}
          </Button>
        </>
      )}
    </div>
  );
}
